/**
 * Fable F7: `_enforce_handoff_operator` (`routers/connections.py`) sends an anonymous OAuth callback to sign-in
 * with `?next=<the exact callback URL, re-queried>` - a plain API path such as
 * `/api/v1/connections/qbo/callback?state=...&code=...`, never a SPA route - so it must survive a REAL
 * navigation, not `react-router`'s client-side one, and only when it is unmistakably ours: a path starting
 * with a single `/` (never `//`, which browsers resolve as protocol-relative to another host) that a `URL`
 * parse against this origin still reports as this origin. Anything else - absent, malformed, or pointed
 * elsewhere - falls back to `location.state.from` exactly as before.
 *
 * Shared by `LoginScreen` and (xmg-sec-mfa) the two second-factor screens, which carry the same `next` on to
 * the end of the sign-in.
 */
export function safeNext(next: string | null | undefined): string | null {
  if (!next || !next.startsWith("/") || next.startsWith("//")) return null;
  try {
    return new URL(next, window.location.origin).origin === window.location.origin ? next : null;
  } catch {
    return null;
  }
}

/** Where a finished sign-in goes: a real navigation to `next` when there is one, else the SPA route `from`. */
export type SignInTarget = { from: string; next: string | null };
